import type { ReactNode } from "react";
import { ChevronDown } from "~/components/icons/central-icons";
import { cn } from "~/lib/utils";

/**
 * Stacked right-rail panel card shared by the stocks, crypto, and explore
 * rails: a page-surface card with a title row, an optional action slot, and
 * the panel body.
 *
 * Pass `onToggle` to make the whole title row a collapse control (the chevron
 * becomes a visual indicator inside the header button); otherwise the header is
 * static and `action` renders any trailing control.
 */
export function SidebarPanel(props: {
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
  contentId?: string;
  contentVisible?: boolean;
  onToggle?: () => void;
  title: string;
  toggleDisabled?: boolean;
  toggleLabel?: string;
}) {
  const contentVisible = props.contentVisible ?? Boolean(props.children);
  const isCollapsible = typeof props.onToggle === "function";

  const title = (
    <h2 className="text-body font-medium text-foreground">{props.title}</h2>
  );
  const chevron = (
    <ChevronDown
      aria-hidden
      className={cn(
        "size-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ease-out group-hover:text-foreground",
        contentVisible ? "rotate-0" : "-rotate-90",
      )}
      strokeWidth={2.25}
    />
  );

  return (
    <section
      className={cn("page-surface-panel rounded-lg p-4", props.className)}
    >
      {isCollapsible ? (
        <button
          aria-controls={props.contentId}
          aria-expanded={contentVisible}
          aria-label={props.toggleLabel}
          className={cn(
            "group -m-1 flex w-full cursor-pointer items-center justify-between gap-3 rounded-md p-1 text-left transition-colors glider-focus-ring disabled:cursor-default",
            contentVisible ? "mb-3" : null,
          )}
          disabled={props.toggleDisabled}
          onClick={props.onToggle}
          type="button"
        >
          {title}
          {chevron}
        </button>
      ) : (
        <div
          className={cn(
            "flex items-center justify-between gap-3",
            contentVisible ? "mb-4" : null,
          )}
        >
          {title}
          {props.action}
        </div>
      )}
      {props.children}
    </section>
  );
}
